import { useMsal } from "@azure/msal-react";
import { getAuthHeader, REACT_APP_PS_PORTAL_API_URI } from "authHelper";
import axios from "axios";
import { useContext, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import BForm from "react-bootstrap/Form";
import * as Yup from "yup";
import dayjs from 'dayjs';
import {
    Button,
    Card,
    Col,
    Container,
    InputGroup,
    Row,
    Toast,
    ToastContainer,
} from "react-bootstrap"
import { useParams } from "react-router-dom"
import { ConfirmDialog } from "components/ConfirmDialog"
import AuthContext from "store/auth-context"
import PropTypes from 'prop-types'
import { FaExclamationTriangle } from 'react-icons/fa'

const PracticeSettings = ({ practice }) => {
    const [showToastMsg, setShowToastMsg] = useState({ visible: false, msg: "" });
    const [stopped, setStopped] = useState(practice.stopped);
    const [deleted, setDeleted] = useState(practice.deleted);
    const [showConfirmClearPracticeReportCache, setShowConfirmClearPracticeReportCache] = useState(false);
    const [showConfirmPracticeStop, setShowConfirmPracticeStop] = useState(false);
    const [showConfirmPracticeDelete, setShowConfirmPracticeDelete] = useState(false);
    const [showConfirmPracticeActivate, setShowConfirmPracticeActivate] =
        useState(false);
    const [showConfirmLinXmartEnable, setShowConfirmLinXmartEnable] =
        useState(false);
    const [showConfirmLinXmartDisable, setShowConfirmLinXmartDisable] =
        useState(false);
    const [pendingLinXmartChecked, setPendingLinXmartChecked] = useState(false);

    const { instance, accounts } = useMsal();
    const ctx = useContext(AuthContext);
    const params = useParams();

    function isSevenDaysAgo(dateString) {
        const sevenDaysInMilliseconds = 7 * 24 * 60 * 60 * 1000;
        const time = Date.parse(dateString);
        return !isNaN(time) && (Date.now() - time) >= sevenDaysInMilliseconds;
    }

    // If there's no stoppedDate, we want to check against the updatedDate.
    // This is to cover any practices which are stopped before delete feature has been rolled out.
    function isSevenDaysOldAndStopped(practice) {
        let result = false; //default to false if null practice.stoppedDate and null practice.updatedDate
        if (practice.stoppedDate && practice.stopped) {
            result = isSevenDaysAgo(practice.stoppedDate);
        }
        else if (practice.updatedDate && practice.stopped) {
            result = isSevenDaysAgo(practice.updatedDate);
        }
        return result;
    }

    async function stopPractice() {
        const authHeader = await getAuthHeader(instance, accounts);
        await axios.post(
            `${REACT_APP_PS_PORTAL_API_URI}/practices/${params.practiceId}/stop`,
            null,
            { headers: authHeader }
        );
        setShowConfirmPracticeStop(false);
        setShowToastMsg({ visible: true, msg: "Practice stopped" });
        setStopped(true);
    }

    async function clearPracticeReportCache() {
        const authHeader = await getAuthHeader(instance, accounts);
        await axios.post(
            `${REACT_APP_PS_PORTAL_API_URI}/practices/${params.practiceId}/clearReportContentCache`,
            null,
            { headers: authHeader }
        );
        setShowConfirmClearPracticeReportCache(false);
        setShowToastMsg({ visible: true, msg: "Practice Report Cache Cleared" });
    }

    async function deletePractice() {
        setShowConfirmPracticeDelete(false);
        const authHeader = await getAuthHeader(instance, accounts);

        try {
            await axios.delete(
                `${REACT_APP_PS_PORTAL_API_URI}/practices/${params.practiceId}`,
                { headers: authHeader }
            );
            setShowToastMsg({ visible: true, msg: 'Practice deleted' });
            setDeleted(true);
        } catch (err) {
            console.error('Delete failed', err);
            setShowToastMsg({
                visible: true,
                msg: 'Failed to delete practice. Please try again.'
            });
        }
    }

    async function activatePractice() {
        const authHeader = await getAuthHeader(instance, accounts);
        await axios.post(
            `${REACT_APP_PS_PORTAL_API_URI}/practices/${params.practiceId}/activate`,
            null,
            { headers: authHeader }
        );
        setShowConfirmPracticeActivate(false);
        setShowToastMsg({ visible: true, msg: "Practice enabled" });
        setStopped(false);
    }

    const validationSchema = Yup.object().shape({
        alerts: Yup.boolean(),
        alertsPollingCount: Yup.number()
            .label("Alerts Polling Count")
            .required("Alerts Polling Count is required")
            .min(1)
            .max(20),
        alertsPollingPeriod: Yup.number()
            .label("Alerts Polling Period")
            .required("Alerts Polling Period is required")
            .min(2000)
            .max(120000),
        prompts: Yup.boolean(),
        promptsPollingCount: Yup.number()
            .label("Prompts Polling Count")
            .required("Prompts Polling Count is required")
            .min(1)
            .max(20),
        promptsPollingPeriod: Yup.number()
            .label("Prompts Polling Period")
            .required("Prompts Polling Period is required")
            .min(2000)
            .max(120000),
        extractPollingPeriod: Yup.number()
            .label("Extract Polling Period")
            .required("Extract Polling Period is required")
            .min(1)
            .max(3600),
        extractRecordCount: Yup.number()
            .label("Extract Record Count")
            .required("Extract Record Count is required")
            .min(1),
        firstRunExtractPollingPeriod: Yup.number()
            .label("Quiet Hour Extract Polling Period")
            .required("Quiet Hour Extract Polling Period is required")
            .min(1)
            .max(3600),
        firstRunExtractRecordCount: Yup.number()
            .label("Quiet Hour Extract Record Count")
            .required("Quiet Hour Extract Record Count is required")
            .min(1),
        extractQuietStartHour: Yup.number()
            .label("Quiet Start Hour")
            .required("Quiet Start Hour is required")
            .min(0)
            .max(23),
        extractQuietEndHour: Yup.number()
            .label("Quiet End Hour")
            .required("Quiet End Hour is required")
            .min(0)
            .max(23),
        patientSharing: Yup.boolean(),
        isLinXmart: Yup.boolean(),
        queryThrottlePeriod: Yup.number()
            .label("Query Throttle Period")
            .min(0)
            .max(5000),
    });

    return (
        <>
            <ToastContainer className="p-3" position="bottom-center">
                <Toast
                    onClose={() => setShowToastMsg({ visible: false, msg: "" })}
                    show={showToastMsg.visible}
                    delay={3000}
                    autohide>
                    <Toast.Header>
                        <strong className="me-auto">Primary Sense</strong>
                    </Toast.Header>
                    <Toast.Body>{showToastMsg.msg}</Toast.Body>
                </Toast>
            </ToastContainer>

            <Formik
                enableReinitialize={true}
                initialValues={practice.settings}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                    const authHeader = await getAuthHeader(instance, accounts);
                    await axios.put(
                        `${REACT_APP_PS_PORTAL_API_URI}/practices/${params.practiceId}/settings`,
                        values,
                        { headers: authHeader }
                    );
                    resetForm({ values });
                    setSubmitting(false);
                    setShowToastMsg({ visible: true, msg: "Settings saved" });
                }}>
                {({
                    errors,
                    touched,
                    isSubmitting,
                    dirty,
                    resetForm,
                    values,
                    setFieldValue,
                }) => (
                    <>
                        <Container>
                            <Form>
                                <Row className="mb-3 gy-3">
                                    <Col xs={4} className="d-flex align-items-center">
                                        <div className="form-check">
                                            <Field
                                                id="alertsCheckField"
                                                type="checkbox"
                                                name="alerts"
                                                className="form-check-input"
                                                disabled={!ctx.isAdmin}
                                            />
                                            <BForm.Label htmlFor="alertsCheckField" className="form-check-label">Alerts</BForm.Label>
                                        </div>
                                    </Col>
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="alertsPollingCountField" >Alerts Polling Count</BForm.Label>
                                            <Field
                                                id="alertsPollingCountField"
                                                type="text"
                                                name="alertsPollingCount"
                                                className="form-control"
                                                disabled={!ctx.isAdmin}
                                            />
                                            <ErrorMessage
                                                name="alertsPollingCount"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="alertsPollingPeriodField" >Alerts Polling Period</BForm.Label>
                                            <InputGroup>
                                                <Field
                                                    id="alertsPollingPeriodField"
                                                    type="text"
                                                    name="alertsPollingPeriod"
                                                    className="form-control"
                                                    disabled={!ctx.isAdmin}
                                                />
                                                <InputGroup.Text>milliseconds</InputGroup.Text>
                                            </InputGroup>
                                            <ErrorMessage
                                                name="alertsPollingPeriod"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3 gy-3">
                                    <Col xs={4} className="d-flex align-items-center">
                                        <div className="form-check">
                                            <Field
                                                id="promptsCheckField"
                                                type="checkbox"
                                                name="prompts"
                                                className="form-check-input"
                                                disabled={!ctx.isAdmin}
                                            />
                                            <BForm.Label htmlFor="promptsCheckField" className="form-check-label">Prompts</BForm.Label>
                                        </div>
                                    </Col>
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="promptsPollingCountField" >Prompts Polling Count</BForm.Label>
                                            <Field
                                                id="promptsPollingCountField"
                                                type="text"
                                                name="promptsPollingCount"
                                                className="form-control"
                                                disabled={!ctx.isAdmin}
                                            />
                                            <ErrorMessage
                                                name="promptsPollingCount"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="promptsPollingPeriodField" >Prompts Polling Period</BForm.Label>
                                            <InputGroup>
                                                <Field
                                                    id="promptsPollingPeriodField"
                                                    type="text"
                                                    name="promptsPollingPeriod"
                                                    className="form-control"
                                                    disabled={!ctx.isAdmin}
                                                />
                                                <InputGroup.Text>milliseconds</InputGroup.Text>
                                            </InputGroup>
                                            <ErrorMessage
                                                name="promptsPollingPeriod"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3 gy-3">
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="extractPollingPeriodField" >Extract Polling Period</BForm.Label>
                                            <InputGroup>
                                                <Field
                                                    id="extractPollingPeriodField"
                                                    type="text"
                                                    name="extractPollingPeriod"
                                                    className="form-control"
                                                    disabled={!ctx.isAdmin}
                                                />
                                                <InputGroup.Text>seconds</InputGroup.Text>
                                            </InputGroup>
                                            <ErrorMessage
                                                name="extractPollingPeriod"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={4} className="d-flex">
                                        <BForm.Group>
                                            <BForm.Label htmlFor="extractRecordCountField" >Extract Record Count</BForm.Label>
                                            <Field
                                                id="extractRecordCountField"
                                                type="text"
                                                name="extractRecordCount"
                                                className="form-control"
                                                disabled={!ctx.isAdmin}
                                            />
                                            <ErrorMessage
                                                name="extractRecordCount"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="commandTimeoutField" >Command Timeout</BForm.Label>
                                            <Field
                                                id="commandTimeoutField"
                                                type="text"
                                                name="commandTimeout"
                                                disabled={true}
                                                className="form-control"
                                            />
                                        </BForm.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3 gy-3">
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="quietHrExtrctPollPerField" >
                                                Quiet Hour - Extract Polling Period
                                            </BForm.Label>
                                            <InputGroup>
                                                <Field
                                                    id="quietHrExtrctPollPerField"
                                                    type="text"
                                                    name="firstRunExtractPollingPeriod"
                                                    className="form-control"
                                                    disabled={!ctx.isAdmin}
                                                />
                                                <InputGroup.Text>seconds</InputGroup.Text>
                                            </InputGroup>
                                            <ErrorMessage
                                                name="firstRunExtractPollingPeriod"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={4} className="d-flex">
                                        <BForm.Group>
                                            <BForm.Label htmlFor="quietHrExtrctRecCountField" >
                                                Quiet Hour - Extract Record Count
                                            </BForm.Label>
                                            <Field
                                                id="quietHrExtrctRecCountField"
                                                type="text"
                                                name="firstRunExtractRecordCount"
                                                className="form-control"
                                                disabled={!ctx.isAdmin}
                                            />
                                            <ErrorMessage
                                                name="firstRunExtractRecordCount"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="serverField" >Server</BForm.Label>
                                            <Field
                                                id="serverField"
                                                type="text"
                                                name="server"
                                                disabled={true}
                                                className="form-control"
                                            />
                                        </BForm.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3 gy-3">
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="qryThrottlePeriodField" >Query Throttle Period</BForm.Label>
                                            <InputGroup>
                                                <Field
                                                    id="qryThrottlePeriodField"
                                                    type="text"
                                                    name="queryThrottlePeriod"
                                                    className="form-control"
                                                    disabled={!ctx.isAdmin}
                                                />
                                                <InputGroup.Text>milliseconds</InputGroup.Text>
                                            </InputGroup>
                                            <ErrorMessage
                                                name="queryThrottlePeriod"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={2} className="d-flex">
                                        <BForm.Group>
                                            <BForm.Label htmlFor="quietStartHrField" >Quiet Start Hour</BForm.Label>
                                            <Field
                                                id="quietStartHrField"
                                                type="text"
                                                name="extractQuietStartHour"
                                                className="form-control"
                                                disabled={!ctx.isAdmin}
                                            />
                                            <ErrorMessage
                                                name="extractQuietStartHour"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={2} className="d-flex">
                                        <BForm.Group>
                                            <BForm.Label htmlFor="quietEndHrField" >Quiet End Hour</BForm.Label>
                                            <Field
                                                id="quietEndHrField"
                                                type="text"
                                                name="extractQuietEndHour"
                                                className="form-control"
                                                disabled={!ctx.isAdmin}
                                            />
                                            <ErrorMessage
                                                name="extractQuietEndHour"
                                                component="div"
                                                className="invalid-feedback d-block"
                                            />
                                        </BForm.Group>
                                    </Col>
                                    <Col xs={4}>
                                        <BForm.Group>
                                            <BForm.Label htmlFor="apiPortField" >Api Port</BForm.Label>
                                            <Field
                                                id="apiPortField"
                                                type="text"
                                                name="apiPort"
                                                disabled={true}
                                                className="form-control"
                                            />
                                        </BForm.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3 gy-3">
                                    <Col xs={4} className="d-flex align-items-center">
                                        <div className="form-check">
                                            <BForm.Label className="form-check-label">
                                                <Field
                                                    type="checkbox"
                                                    name="patientSharing"
                                                    className="form-check-input"
                                                    disabled={!ctx.isAdmin}
                                                />
                                                {' '}Patient Sharing
                                            </BForm.Label>
                                        </div>
                                    </Col>
                                    <Col xs={4} className="d-flex align-items-center">
                                        <div className="form-check">
                                            <BForm.Label className="form-check-label">
                                                <input
                                                    type="checkbox"
                                                    name="isLinXmart"
                                                    className="form-check-input"
                                                    disabled={!ctx.isAdmin}
                                                    checked={values.isLinXmart}
                                                    onChange={(e) => {
                                                        const willBe = e.target.checked;
                                                        // stash the choice
                                                        setPendingLinXmartChecked(willBe);
                                                        // and show the right dialog
                                                        if (willBe && !values.isLinXmart) {
                                                            setShowConfirmLinXmartEnable(true);
                                                        } else if (!willBe && values.isLinXmart) {
                                                            setShowConfirmLinXmartDisable(true);
                                                        }
                                                    }}
                                                />
                                                {' '}LinXmart Participating
                                            </BForm.Label>
                                        </div>
                                        <ErrorMessage
                                            name="isLinXmart"
                                            component="div"
                                            className="invalid-feedback d-block"
                                        />
                                    </Col>
                                    <Col xs={4} className="d-flex align-items-center">
                                        <div className="form-check">
                                            <BForm.Label className="form-check-label">
                                                <Field
                                                    type="checkbox"
                                                    name="isBeta"
                                                    className="form-check-input"
                                                    disabled={!ctx.isAdmin}
                                                />
                                                Beta Channel Opt-In
                                            </BForm.Label>
                                        </div>
                                    </Col>
                                </Row>
                                {ctx.isAdmin && (
                                    <Row className="mt-5">
                                        <Col>
                                            <Button
                                                type="submit"
                                                variant="outline-primary"
                                                disabled={isSubmitting || !dirty}>
                                                Save{" "}
                                            </Button>

                                            <Button
                                                className="ms-3"
                                                variant="outline-primary"
                                                disabled={!dirty}
                                                onClick={() => resetForm()}>
                                                Reset
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                            </Form>
                        </Container>
                        {showConfirmLinXmartDisable && (
                            <ConfirmDialog
                                title="Confirm LinXmart Disable"
                                body="Disabling LinXmart participation will result in previously collected data being permanently deleted from the database for the selected practice, outside of hours. This change will be commited when you press Save."
                                onconfirm={() => {
                                    setFieldValue("isLinXmart", pendingLinXmartChecked);
                                    setShowConfirmLinXmartDisable(false);
                                }}
                                onhide={() => setShowConfirmLinXmartDisable(false)}
                            />
                        )}
                        {showConfirmLinXmartEnable && (
                            <ConfirmDialog
                                title="Confirm LinXmart Enable"
                                body="Enabling LinXmart participation will trigger an extraction for the selected practice, outside of hours. This change will be commited when you press Save."
                                onconfirm={() => {
                                    setFieldValue("isLinXmart", pendingLinXmartChecked);
                                    setShowConfirmLinXmartEnable(false);
                                }}
                                onhide={() => setShowConfirmLinXmartEnable(false)}
                            />
                        )}
                    </>
                )}
            </Formik>
            {ctx.isAdmin && (
                <Container>
                    <Row className="mt-4">
                        <Col>
                            {!stopped && (
                            <Card className="mt-4">
                                <Card.Body>
                                    <Card.Title>

                                            <Button
                                                type="button" className="me-2"
                                                variant="warning"
                                                aria-label="Clear Report Cache"
                                                onClick={() => setShowConfirmClearPracticeReportCache(true)}>
                                                Clear Report Cache
                                            </Button>

                                        {showConfirmClearPracticeReportCache && (
                                            <ConfirmDialog
                                                id="confirm-disable-practice"
                                                aria-labelledby="confirm-disable-practice-title"
                                                aria-describedby="confirm-disable-practice-body"
                                                title="Confirm Clear Report Cache"
                                                body="Do you want to clear the report cache?"
                                                onconfirm={clearPracticeReportCache}
                                                onhide={() => setShowConfirmClearPracticeReportCache(false)}
                                            />
                                        )}
                                    </Card.Title>
                                    <Card.Text>
                                            This will clear the report cache for this practice. Any reports that are newly created will be generated from current patient data for this practice.
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                             )}
                            <Card className="mt-4">
                                <Card.Body>
                                    <Card.Title>
                                        {!stopped && (
                                            <Button
                                                type="button"
                                                variant="warning"
                                                aria-label="Disable practice"
                                                onClick={() => setShowConfirmPracticeStop(true)}>
                                                Disable Practice
                                            </Button>
                                        )}
                                        {showConfirmPracticeStop && (
                                            <ConfirmDialog
                                                id="confirm-disable-practice"
                                                aria-labelledby="confirm-disable-practice-title"
                                                aria-describedby="confirm-disable-practice-body"
                                                title="Confirm disable practice"
                                                body="Do you want to disable the practice?"
                                                onconfirm={stopPractice}
                                                onhide={() => setShowConfirmPracticeStop(false)}
                                            />
                                        )}
                                        {stopped && (
                                            <>
                                                <p className="text-danger">
                                                    Practice has been stopped{" "}
                                                    {practice.stoppedDate &&
                                                        "since " +
                                                        dayjs(practice.stoppedDate).format("DD/MM/YYYY")}
                                                </p>
                                                <Button
                                                    type="button" className="me-2"
                                                    aria-label="Enable practice"
                                                    onClick={() => setShowConfirmPracticeActivate(true)}>
                                                    Enable Practice
                                                </Button>
                                            </>
                                        )}
                                        {showConfirmPracticeActivate && (
                                            <ConfirmDialog
                                                id="confirm-enable-practice"
                                                aria-labelledby="confirm-enable-practice-title"
                                                aria-describedby="confirm-enable-practice-body"
                                                title="Confirm enable practice"
                                                body="Do you want to enable the practice?"
                                                onconfirm={activatePractice}
                                                onhide={() => setShowConfirmPracticeActivate(false)}
                                            />
                                        )}
                                        {stopped && !deleted &&
                                            <Button
                                                type="button"
                                                // gray when disabled, red when enabled
                                                variant={isSevenDaysOldAndStopped(practice) && stopped && !deleted ? 'danger' : 'secondary'}
                                                disabled={!(isSevenDaysOldAndStopped(practice) && stopped && !deleted)}
                                                aria-label={
                                                    isSevenDaysOldAndStopped(practice)
                                                        ? "Delete practice"
                                                        : "Delete practice (available after 7 days stopped)"
                                                }
                                                onClick={() => setShowConfirmPracticeDelete(true)}
                                            >
                                                Delete Practice
                                            </Button>
                                        }
                                        {showConfirmPracticeDelete &&
                                            <ConfirmDialog
                                                id="confirm-delete-practice"
                                                aria-labelledby="confirm-delete-practice-title"
                                                aria-describedby="confirm-delete-practice-body"
                                            title={
                                                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                                    <FaExclamationTriangle color="red" />
                                                    Confirm delete practice
                                                </span>
                                            }
                                            body="WARNING: This will delete this practice and all data associated with this practice! Are you sure?"
                                                onconfirm={deletePractice}
                                                onhide={() => setShowConfirmPracticeDelete(false)}
                                            />
                                        }
                                    </Card.Title>
                                    {!stopped && (
                                        <Card.Text>
                                            Disabling a practice will disable both data extractor and desktop
                                            application, so that data flow between the practice and
                                            Primary Sense is switched off.
                                        </Card.Text>
                                    )}
                                    {stopped && !deleted && (
                                        <Card.Text>
                                            After a 7-day cooling period of being stopped, a practice can be deleted.
                                        </Card.Text>
                                    )}

                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            )}
        </>
    );
};

PracticeSettings.propTypes = {
    practice: PropTypes.shape({
        stopped: PropTypes.bool,
        stoppedDate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date)
        ]),
        updatedDate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date)
        ]),
        deleted: PropTypes.bool,
    }).isRequired,
};

export default PracticeSettings;
